import { type VariantProps, cva } from 'class-variance-authority'
import { Slot } from 'radix-ui'
import * as React from 'react'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  /* cursor-pointer is in the base so every Button gets it for free, no
   * per-call `className='cursor-pointer'` sprinkling. disabled buttons
   * still feel inert because `disabled:pointer-events-none` short-
   * circuits cursor + click entirely. */
  "group/button inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md border border-transparent bg-clip-padding text-xs/relaxed font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/80',
        outline:
          'border-border hover:bg-input/50 hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:bg-input/30',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground',
        ghost:
          'hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50',
        destructive:
          'bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40',
        link: 'text-primary underline-offset-4 hover:underline'
      },
      size: {
        default:
          "h-11 gap-1 px-3 text-xs/relaxed has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 sm:h-7 sm:px-2 [&_svg:not([class*='size-'])]:size-3.5",
        xs: "h-9 gap-1 rounded-sm px-2 text-[0.625rem] has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 sm:h-5 [&_svg:not([class*='size-'])]:size-2.5",
        sm: "h-10 gap-1 px-3 text-xs/relaxed has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 sm:h-6 sm:px-2 [&_svg:not([class*='size-'])]:size-3",
        lg: "h-11 gap-1 px-3 text-xs/relaxed has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 sm:h-8 sm:px-2.5 [&_svg:not([class*='size-'])]:size-4",
        /* hero: chunky h-9 rounded-xl primary for page-hero action rows
         * (matches the example app's contractor/client-detail pattern).
         * use when a detail-page hero has 1–3 actions worth giving real
         * presence to. xl (h-11) stays for the bigger New-Conversation
         * style CTA on list pages.
         */
        hero: "h-9 gap-1.5 rounded-xl px-3 text-sm has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5 [&_svg:not([class*='size-'])]:size-4",
        pill: "h-11 gap-1.5 rounded-lg px-3 text-[13px] sm:h-8 [&_svg:not([class*='size-'])]:size-3.5",
        xl: "h-11 gap-1.5 rounded-xl px-5 text-[15px] font-medium has-data-[icon=inline-end]:pr-4 has-data-[icon=inline-start]:pl-4 [&_svg:not([class*='size-'])]:size-4",
        icon: "size-11 sm:size-7 [&_svg:not([class*='size-'])]:size-3.5",
        'icon-xs':
          "size-9 rounded-sm sm:size-5 [&_svg:not([class*='size-'])]:size-2.5",
        'icon-sm': "size-10 sm:size-6 [&_svg:not([class*='size-'])]:size-3",
        'icon-lg': "size-11 sm:size-8 [&_svg:not([class*='size-'])]:size-4"
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
)

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : 'button'

  return (
    <Comp
      data-slot='button'
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
